/**
 * Swamithra — the rich-text contract shared by every composer in the product:
 * expert responses, vetting reviews, a taxpayer's follow-up message and a
 * vetting request's notes.
 *
 * Composing offers bold, italic, underline, ordered and unordered lists, line
 * breaks and a closed set of font sizes; the markup is stored on the field it
 * was composed for (`bodyRich`, `reviewBodyRich`, or — for the follow-up and
 * vetting-notes fields — directly on `body`/`notes`, which carry markup rather
 * than plain text) and has to render identically in the panel and in the user
 * app (US-01-001.14, US-01-004.8).
 *
 * WHERE SANITISATION HAPPENS
 * ==========================
 * SERVER-SIDE, BEFORE STORAGE. US-01-001.14 is explicit: "any script tag, event
 * handler or unlisted element in the submitted markup is stripped server-side
 * before storage". This module deliberately does NOT ship a hand-written HTML
 * sanitiser. A sanitiser is the wrong thing to write from scratch — the failure
 * mode is silent and the attack surface is the whole product — and one that
 * lived here would invite a renderer to trust it instead of the server.
 *
 * What this module does own is the ALLOW-LIST, so the editor's toolbar, the
 * server's stripper and the renderer's configuration are the same list; and the
 * emptiness rule, so "submitting an empty review is blocked" means the same
 * thing everywhere (US-01-004.8).
 */

import { z } from "zod"

import { RICH_TEXT_MAX_LENGTH } from "./constants"

/**
 * Every element composed markup may contain.
 *
 * Nothing that loads a resource, nothing that takes a URL, nothing that can
 * carry an event handler. `span` carries only a font-size token from
 * `RICH_TEXT_FONT_SIZE_CLASSES` — never a free-form `style` attribute, so the
 * server's allow-list stays a list of exact class names rather than a CSS
 * parser.
 */
export const RICH_TEXT_ALLOWED_TAGS: readonly string[] = [
	"p",
	"br",
	"b",
	"strong",
	"i",
	"em",
	"u",
	"ul",
	"ol",
	"li",
	"span",
]

/**
 * The closed set of font-size tokens the editor's dropdown may emit, as
 * `<span class="…">`.
 *
 * A closed set rather than an arbitrary `style="font-size:…"` — the server
 * allow-lists these four exact class names instead of parsing a CSS value,
 * which keeps the sanitiser a list lookup instead of a CSS parser.
 */
export const RICH_TEXT_FONT_SIZE_CLASSES = ["rt-fs-sm", "rt-fs-md", "rt-fs-lg", "rt-fs-xl"] as const

export type RichTextFontSizeClass = (typeof RICH_TEXT_FONT_SIZE_CLASSES)[number]

/**
 * Plain text from markup, for an emptiness check, a character count, or the
 * one-line summary a notification carries.
 *
 * Not a sanitiser and not safe to feed back into `innerHTML` — it produces TEXT.
 * Entities are decoded for the five that a rich-text editor actually emits;
 * anything else stays escaped, which is the safe direction for a value that is
 * about to be measured rather than rendered.
 */
export function richTextToPlain(markup: string): string {
	return markup
		.replace(/<br\s*\/?>/gi, " ")
		.replace(/<\/(p|li|ul|ol)>/gi, " ")
		.replace(/<[^>]*>/g, "")
		.replace(/&nbsp;/gi, " ")
		.replace(/&amp;/gi, "&")
		.replace(/&lt;/gi, "<")
		.replace(/&gt;/gi, ">")
		.replace(/&quot;/gi, '"')
		.replace(/\s+/g, " ")
		.trim()
}

const NAMED_ENTITY_TEXT: Readonly<Record<string, string>> = {
	amp: "&",
	lt: "<",
	gt: ">",
	quot: '"',
	apos: "'",
	nbsp: " ",
}

/**
 * The number of characters a reader sees in markup: the "n" in the form's
 * "n/3000" counter, and the number the description and notes limits apply to.
 *
 * Tags are not counted, an entity counts as the one character it stands for,
 * and line breaks are not counted, so formatting does not eat into the limit.
 * Counted in code points, so an Indic letter or a ₹ is one character.
 *
 * Mirrors `richtext.VisibleLength` on the server, which makes the same 422
 * decision: the two share their test cases, and a change to one without the
 * other puts the counter and the server out of step.
 */
export function richTextVisibleLength(markup: string): number {
	const text = markup
		.replace(/<[^>]*>/g, "")
		.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z]+);/gi, (entity, name: string) => {
			if (name.startsWith("#")) return "."
			return NAMED_ENTITY_TEXT[name.toLowerCase()] === undefined ? entity : "."
		})
		.replace(/[\r\n]/g, "")
	return [...text].length
}

/**
 * The schema for a rich-text field limited by what the user sees, with the
 * stored markup held to the server's storage bound as well.
 */
export function visibleRichTextSchema(maxLength: number, tooLong: string) {
	return z
		.string()
		.max(RICH_TEXT_MAX_LENGTH, "This has too much formatting. Remove some and try again.")
		.refine(value => richTextVisibleLength(value) <= maxLength, tooLong)
}

/**
 * True when markup carries no words.
 *
 * `<p><br></p>` is what every rich-text editor leaves behind when the user
 * clears the box, and a length check on the markup would call it 11 characters
 * of content.
 */
export function isRichTextEmpty(markup: string): boolean {
	return richTextToPlain(markup).length === 0
}

/**
 * The schema for a composed response or review.
 *
 * The length limit is measured on the MARKUP, because that is what is stored and
 * what the server's 422 will be about.
 */
export const richTextSchema = z
	.string()
	.max(RICH_TEXT_MAX_LENGTH, `Keep it under ${RICH_TEXT_MAX_LENGTH} characters.`)
	.refine(value => !isRichTextEmpty(value), "Write a response before sending.")

/**
 * A one-line summary for a list row or a notification body.
 *
 * Truncates on a word boundary where it can. The WhatsApp templates carry only
 * an id, a summary and a link (US-00-006.17) — this is what "a summary" is
 * allowed to be, and it is built from the same function everywhere so it cannot
 * accidentally become the whole response text in one caller.
 */
export function summarise(markup: string, maxLength = 80): string {
	const plain = richTextToPlain(markup)
	if (plain.length <= maxLength) return plain
	const cut = plain.slice(0, maxLength)
	const lastSpace = cut.lastIndexOf(" ")
	return `${(lastSpace > maxLength / 2 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`
}
